import {combineReducers} from '@modifi/redux'
import {routerActions} from '@modifi/router'
import {ACTION_TYPES} from './constants'
import {matchTypeRequest} from './config'
import {getSplitAmounts} from './utils'
import {aSplitTransaction} from '../../../modules/split-transaction'
import {aFetchTransaction} from '../../../data/transaction'
import {selectBuyerReduction} from './selectors'
import {
  MATCH_TYPE_PAYMENT_REPAYMENT,
  BULK_MATCHING_COMMENT,
  MATCH_TYPE_PAYMENT_PASS_THROUGH,
} from '../constants'

const initialState = {
  pending: false,
  error: null,
  completed: false,
}

const submitReducer = (state = initialState, {type, payload}) => {
  switch (type) {
    case ACTION_TYPES.MATCH_POST:
      return {
        ...state,
        pending: {
          ...state.pending,
          [payload.id]: true,
        },
      }
    case ACTION_TYPES.MATCH_SUCCESS:
      return {
        ...state,
        pending: {
          ...state.pending,
          [payload.id]: false,
        },
        completed: {
          ...state.completed,
          [payload.id]: true,
        },
      }
    case ACTION_TYPES.MATCH_ERROR:
      return {
        ...state,
        pending: {
          ...state.pending,
          [payload.id]: false,
        },
        error: {
          ...state.error,
          [payload.id]: {
            code: payload.errorCode,
            message: payload.errorMessage,
            contextParams: payload.contextParameters,
          },
        },
        completed: {
          ...state.completed,
          [payload.id]: true,
        },
      }
    case ACTION_TYPES.MATCH_CLEANUP:
      return {
        ...state,
        error: null,
        pending: false,
        completed: false,
      }
    default:
      return state
  }
}

const initialFeatureState = {
  selectedBuyerReduction: [],
}

const featureReducer = (state = initialFeatureState, {type, payload}) => {
  switch (type) {
    case ACTION_TYPES.SET_BUYER_REDUCTION: {
      return {
        ...state,
        selectedBuyerReduction: payload,
      }
    }
    case ACTION_TYPES.CLEAR_BUYER_REDUCTION: {
      return initialFeatureState
    }
    default:
      return state
  }
}

export default combineReducers({
  submit: submitReducer,
  feature: featureReducer,
})

const aMatchTransaction = data => async (dispatch, getState) => {
  dispatch({
    type: ACTION_TYPES.MATCH_POST,
    payload: {
      id: data?.targetEntity?.id,
    },
  })

  try {
    const request = matchTypeRequest[data?.matchType]
    await request(getState(), data)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error)
    throw error
  }
}

export const aConfirmMatch = data => async (dispatch, getState) => {
  const {targetEntities: entities, ...restData} = data
  const buyerReductions = selectBuyerReduction(getState())

  // add buyer reduction selection only for payment repayments
  const targetEntities = [MATCH_TYPE_PAYMENT_REPAYMENT, MATCH_TYPE_PAYMENT_PASS_THROUGH].includes(
    data?.matchType
  )
    ? entities.map(props => ({
        isBuyerReduction: buyerReductions.includes(props?.externalId),
        ...props,
      }))
    : entities

  // get split status
  const {canSplit, parts} = getSplitAmounts(data?.sourceEntity, targetEntities)
  let result = [data?.sourceEntity]
  if (canSplit) {
    try {
      result = await dispatch(
        aSplitTransaction({
          transactionId: data?.sourceEntity?.id,
          changes: {
            ...parts,
            comment: BULK_MATCHING_COMMENT,
          },
        })
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      throw e
    }
  }

  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const [index, entity] of targetEntities.entries()) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(
          aMatchTransaction({...restData, sourceEntity: result[index], targetEntity: entity})
        )
        dispatch({
          type: ACTION_TYPES.MATCH_SUCCESS,
          payload: {
            id: entity?.id,
          },
        })
      } catch (error) {
        if (error.data) {
          const responseBody = error.data()
          dispatch({
            type: ACTION_TYPES.MATCH_ERROR,
            payload: {
              errorCode: responseBody?.errorCode,
              errorMessage: responseBody.errorMessage || responseBody.httpError || 'Internal Error',
              contextParameters: responseBody?.contextParameters,
              id: entity?.id,
            },
          })
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error)
  }

  // transaction will take time to flag as processed
  // hence we need to redirect manually
  dispatch(aFetchTransaction(data?.sourceEntity?.id))

  return dispatch(routerActions.replace('/'))
}

export const aCleanupMatch = () => dispatch => {
  dispatch({
    type: ACTION_TYPES.MATCH_CLEANUP,
  })
}

// for payment repayment
export const aSetBuyerReduction = id => (dispatch, getState) => {
  const selectedBuyerReduction = selectBuyerReduction(getState())
  if (selectedBuyerReduction.find(paymentId => paymentId === id)) {
    dispatch({
      type: ACTION_TYPES.SET_BUYER_REDUCTION,
      payload: selectedBuyerReduction.filter(selectedId => selectedId !== id),
    })
  } else {
    dispatch({
      type: ACTION_TYPES.SET_BUYER_REDUCTION,
      payload: [...selectedBuyerReduction, id],
    })
  }
}
